/**
 * Desktop's implementation of the SDK's `VoiceModePlatform` contract: browser
 * microphone capture, browser audio playback, the app's sound set, the transcript
 * send path (`sendVoiceTranscript`), and Electron/analytics side effects.
 * `attachDesktopVoiceModePlatform` hands it to the SDK, which runs sessions
 * until the returned cleanup detaches it.
 */

import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { reaction } from 'mobx';

import { electronApis } from '@/features/electron-api';
import {
  playSound,
  startThinkingChirps,
  type ThinkingChirps,
} from '@/features/sounds/sounds';
import {
  createCaptureController,
  type CaptureController,
} from '@/features/speech-input/capture-controller';
import { claimSpeechActivity } from '@/features/speech-input/speech-activity-handoff';
import { littlebirdSdk } from '@/lib/sdk-init';
import { settingsStore } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';

import { sendVoiceTranscript } from '../chat-send';

import {
  BrowserAudioPlayback,
  logResponseAudioEvent,
} from './browser-audio-playback';

import type { VoiceCaptureCallbacks, VoiceModePlatform } from '@littlebird/sdk';

const THINKING_QUIET_MS = 10_000;

type ActiveVoiceCapture = {
  capture: CaptureController | null;
  callbacks: VoiceCaptureCallbacks | null;
};
// The active session's capture, kept desktop-side for the composer's waveform.
// Cleared only on platform detach, so between sessions it can still point at
// the previous session's disposed controller, which reports a null stream.
// Lives on globalThis: dev hot reload can leave two live copies of this
// module, and every copy must see the same active capture.
// TODO: Expose the active capture from the SDK's voice mode instead of
// mirroring it here. The SDK survives hot reload, so that also removes the
// need for the globalThis workaround.
const activeVoiceCapture = ((
  globalThis as { __littlebirdActiveVoiceCapture?: ActiveVoiceCapture }
).__littlebirdActiveVoiceCapture ??= { capture: null, callbacks: null });

/** Return the microphone stream of the active session's current recording, or null */
export function getVoiceModeMediaStream(): MediaStream | null {
  return activeVoiceCapture.capture?.mediaStreamRef.current ?? null;
}

/**
 * Inject a development-harness transcript through the active session's
 * `VoiceCaptureCallbacks.onTranscript`
 */
export function submitVoiceModeTestTranscript(transcript: string): void {
  activeVoiceCapture.callbacks?.onTranscript(transcript);
}

/**
 * Build the platform handed to `VoiceModeStore.attachPlatform`. Every
 * machine created while the platform stays attached shares it.
 */
export function createDesktopVoiceModePlatform(): VoiceModePlatform {
  let releaseSpeechActivityClaim: (() => void) | null = null;
  let releaseBackgroundTimers: (() => void) | null = null;

  return {
    // The controller satisfies the SDK's capture-handle contract directly
    createCapture: (callbacks) => {
      const capture = createCaptureController(
        callbacks,
        // Voice Mode claims speech activity for the whole session
        false
      );
      activeVoiceCapture.callbacks = callbacks;
      activeVoiceCapture.capture = capture;
      return capture;
    },
    createPlayback: () => new BrowserAudioPlayback(),
    playListeningCue: () => playSound('listen-open'),
    playSubmittedCue: () => playSound('listen-close'),
    playNoSpeechCue: () => playSound('nothing'),
    playFailureCue: () => playSound('problem'),
    playHangupCue: () => playSound('sign-off'),
    playStopCue: () => playSound('listen-close'),
    startThinkingCue: () => {
      let chirps: ThinkingChirps | null = null;
      const stopWatchingSetting = reaction(
        () => settingsStore.voiceModeThinkingSoundEnabled,
        (enabled) => {
          chirps?.stop();
          chirps = enabled ? startThinkingChirps(THINKING_QUIET_MS) : null;
        },
        { fireImmediately: true }
      );
      const stepSubscription = globalEventBus.on(
        EventNames.CHAT_ASSISTANT_DID_FINISH_STEP,
        ({ data }) => {
          if (data.threadId === littlebirdSdk.voiceMode.session?.draftId) {
            chirps?.chirpForStep();
          }
        }
      );
      return () => {
        stopWatchingSetting();
        stepSubscription.unsubscribe();
        chirps?.stop();
      };
    },
    // Reuse the mounted chat input's send setup when available; otherwise send
    // the transcript without input-specific context or draft handling
    sendTranscript: sendVoiceTranscript,
    getSubmittedCueDelayMs: () => settingsStore.voiceModeSubmittedSoundDelayMs,
    onSessionStart: (draftId) => {
      releaseSpeechActivityClaim = claimSpeechActivity(() =>
        littlebirdSdk.voiceMode.reset()
      );
      trackEvent('VOICE_MODE_STARTED', { chatId: draftId });
      // Keep silence detection responsive while the Electron window is hidden
      releaseBackgroundTimers?.();
      releaseBackgroundTimers =
        electronApis.window.keepTimersRunningInBackground();
    },
    onSessionEnd: (draftId) => {
      releaseSpeechActivityClaim?.();
      releaseSpeechActivityClaim = null;
      releaseBackgroundTimers?.();
      releaseBackgroundTimers = null;
      trackEvent('VOICE_MODE_STOPPED', { chatId: draftId });
    },
    logDebugEvent: logResponseAudioEvent,
  };
}

/**
 * Attach desktop's platform pieces to the SDK's Voice Mode for the
 * authenticated app shell's lifetime. The returned cleanup ends any session
 * and detaches, so an unmounted shell can never leave a microphone open.
 */
export function attachDesktopVoiceModePlatform(): () => void {
  const detachPlatform = littlebirdSdk.voiceMode.attachPlatform(
    createDesktopVoiceModePlatform()
  );
  return () => {
    activeVoiceCapture.callbacks = null;
    activeVoiceCapture.capture = null;
    detachPlatform();
  };
}
